import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Lightbulb, Shield, CircleDot, CheckCircle, Users, Calendar, BookOpen, Video, MessageCircle, ArrowRight } from 'lucide-react';
import { membershipBenefits, membershipInclusions, communityImages } from '../data/mock';
import { toast } from 'sonner';

const JoinEWCPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: ''
  });

  const iconMap = {
    Lightbulb,
    Shield,
    CircleDot
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    // Mock submission
    console.log('Membership inquiry submitted:', formData);
    toast.success('Thank you! We\'ll be in touch soon about membership.');

    // Reset form
    setFormData({
      name: '',
      email: '',
      message: ''
    });
  };

  return (
    <div style={{ paddingTop: '80px' }}>
      {/* Hero Section */}
      <section style={{
        background: 'var(--gradient-hero-warm)',
        padding: '5rem 1.5rem',
        textAlign: 'center'
      }}>
        <div className="container" style={{ maxWidth: '900px' }}>
          <div style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '0.5rem',
            background: 'rgba(255, 255, 255, 0.4)',
            borderRadius: '2rem',
            padding: '0.25rem 0.75rem',
            marginBottom: '1.5rem',
            fontSize: '0.75rem',
            textTransform: 'uppercase',
            letterSpacing: '0.025em',
            backdropFilter: 'blur(4px)'
          }}>
            <Users size={14} />
            <span className="font-mono">Community Membership</span>
          </div>

          <h1 className="heading-hero" style={{ marginBottom: '1.5rem' }}>
            The Entrepreneur Wellness Club™
          </h1>
          <p className="body-large" style={{ color: 'var(--text-secondary)', marginBottom: '1rem' }}>
            A wellness-aligned membership for founders who want to build businesses that don't burn them out.
          </p>
          <p className="body-medium" style={{
            color: 'var(--text-primary)',
            fontStyle: 'italic',
            maxWidth: '700px',
            margin: '0 auto'
          }}>
            You deserve a business that grows with you—not at the expense of you.
          </p>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="section" style={{ background: 'var(--bg-page)' }}>
        <div className="container !mx-[10px]">
          <div style={{ textAlign: 'center', marginBottom: '3rem' }}>
            <h2 className="heading-1" style={{ marginBottom: '1rem' }}>
              What You'll Gain
            </h2>
            <p className="body-large" style={{ color: 'var(--text-secondary)' }}>
              Three transformative benefits for sustainable entrepreneurship
            </p>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '2rem' }}>
            {membershipBenefits.map((benefit) => {
              const Icon = iconMap[benefit.icon];
              return (
                <div key={benefit.id} className="card accent-purple" style={{ padding: '2.5rem', textAlign: 'center' }}>
                  <div style={{
                    width: '4rem',
                    height: '4rem',
                    background: 'white',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    margin: '0 auto 1.5rem'
                  }}>
                    <Icon size={32} color="var(--accent-purple-400)" />
                  </div>
                  <h3 className="heading-2" style={{ marginBottom: '1rem' }}>
                    {benefit.title}
                  </h3>
                  <p className="body-medium" style={{ color: 'var(--text-secondary)' }}>
                    {benefit.description}
                  </p>
                </div>);

            })}
          </div>
        </div>
      </section>

      {/* What's Included */}
      <section className="section" style={{ background: 'var(--bg-section)' }}>
        <div className="container !mx-[10px]">
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '3rem', alignItems: 'center' }}>
            <div>
              <h2 className="heading-1" style={{ marginBottom: '1.5rem' }}>
                What's Included
              </h2>
              <p className="body-large" style={{ marginBottom: '2rem', color: 'var(--text-secondary)' }}>
                Everything you need to stay aligned, connected, and supported on your entrepreneurial journey.
              </p>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                {membershipInclusions.map((item, index) =>
                <div key={index} style={{ display: 'flex', gap: '1rem', alignItems: 'flex-start' }}>
                    <CheckCircle size={24} color="var(--text-primary)" style={{ flexShrink: 0, marginTop: '0.125rem' }} />
                    <span className="body-medium" style={{ color: 'var(--text-primary)' }}>
                      {item}
                    </span>
                  </div>
                )}
              </div>
            </div>

            <div>
              <img
                src={communityImages.collaboration1}
                alt="Community collaboration"
                style={{ width: '100%', height: 'auto', borderRadius: '0.75rem', boxShadow: '0 8px 24px rgba(0, 0, 0, 0.08)' }} />

            </div>
          </div>
        </div>
      </section>

      {/* Membership Features */}
      <section className="section" style={{ background: 'var(--bg-page)' }}>
        <div className="container !mx-[10px]">
          <div style={{ textAlign: 'center', marginBottom: '3rem' }}>
            <h2 className="heading-1" style={{ marginBottom: '1rem' }}>
              Member Experience
            </h2>
            <p className="body-large" style={{ color: 'var(--text-secondary)' }}>
              A holistic approach to entrepreneurial wellness
            </p>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '2rem' }}>
            {[
            {
              icon: Calendar,
              title: 'Monthly Alignment Workshops',
              description: 'Live sessions focused on clarity, goal-setting, and sustainable planning. Connect with other members and gain insights.',
              color: 'accent-blue'
            },
            {
              icon: Users,
              title: 'Community Co-Flow Sessions',
              description: 'Work alongside fellow entrepreneurs in structured co-working sessions. Accountability with spaciousness.',
              color: 'accent-green'
            },
            {
              icon: BookOpen,
              title: 'Digital Wellness Hub',
              description: 'Access our library of resources, templates, and wellness practices designed for busy entrepreneurs.',
              color: 'accent-orange'
            },
            {
              icon: Video,
              title: 'Guest Expert Sessions',
              description: 'Learn from specialists in wellness, business strategy, mindfulness, and sustainable growth.',
              color: 'accent-purple'
            },
            {
              icon: MessageCircle,
              title: 'Private Community',
              description: 'Connect with like-minded founders in our exclusive community space. Share wins, challenges, and support.',
              color: 'accent-pink'
            },
            {
              icon: Shield,
              title: 'Emotional Wellness Support',
              description: 'Tools and practices for managing stress, building resilience, and maintaining inner peace.',
              color: 'accent-grey'
            }].
            map((feature, index) => {
              const Icon = feature.icon;
              return (
                <div key={index} className={`card ${feature.color}`} style={{ padding: '2rem' }}>
                  <div style={{
                    width: '3rem',
                    height: '3rem',
                    background: 'white',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginBottom: '1.5rem'
                  }}>
                    <Icon size={24} color="var(--text-primary)" />
                  </div>
                  <h3 className="heading-3" style={{ marginBottom: '0.75rem' }}>
                    {feature.title}
                  </h3>
                  <p className="body-small" style={{ color: 'var(--text-secondary)' }}>
                    {feature.description}
                  </p>
                </div>);

            })}
          </div>
        </div>
      </section>

      {/* Inquiry Form */}
      <section className="section" style={{ background: 'var(--bg-section)' }}>
        <div className="container" style={{ maxWidth: '700px', marginLeft: 'auto', marginRight: 'auto' }}>
          <div style={{ textAlign: 'center', marginBottom: '3rem' }}>
            <h2 className="heading-1" style={{ marginBottom: '1rem' }}>
              Join the Waitlist
            </h2>
            <p className="body-large" style={{ color: 'var(--text-secondary)' }}>
              Interested in membership? Let us know and we'll reach out with more details.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="card" style={{ padding: '2.5rem' }}>
            <div style={{ marginBottom: '1.5rem' }}>
              <label htmlFor="name" className="body-small" style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                Name *
              </label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                style={{
                  width: '100%',
                  padding: '0.75rem 1rem',
                  border: '1px solid var(--border-input)',
                  borderRadius: '0.5rem',
                  fontSize: '1rem',
                  fontFamily: 'inherit',
                  transition: 'border-color 0.2s ease'
                }}
                onFocus={(e) => e.target.style.borderColor = 'var(--border-input-focus)'}
                onBlur={(e) => e.target.style.borderColor = 'var(--border-input)'} />

            </div>

            <div style={{ marginBottom: '1.5rem' }}>
              <label htmlFor="email" className="body-small" style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                Email *
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                style={{
                  width: '100%',
                  padding: '0.75rem 1rem',
                  border: '1px solid var(--border-input)',
                  borderRadius: '0.5rem',
                  fontSize: '1rem',
                  fontFamily: 'inherit',
                  transition: 'border-color 0.2s ease'
                }}
                onFocus={(e) => e.target.style.borderColor = 'var(--border-input-focus)'}
                onBlur={(e) => e.target.style.borderColor = 'var(--border-input)'} />

            </div>

            <div style={{ marginBottom: '1.5rem' }}>
              <label htmlFor="message" className="body-small" style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                Tell us about yourself (optional)
              </label>
              <textarea
                id="message"
                name="message"
                value={formData.message}
                onChange={handleChange}
                rows="4"
                placeholder="What stage is your business in? What are you hoping to gain from the membership?"
                style={{
                  width: '100%',
                  padding: '0.75rem 1rem',
                  border: '1px solid var(--border-input)',
                  borderRadius: '0.5rem',
                  fontSize: '1rem',
                  fontFamily: 'inherit',
                  resize: 'vertical',
                  transition: 'border-color 0.2s ease'
                }}
                onFocus={(e) => e.target.style.borderColor = 'var(--border-input-focus)'}
                onBlur={(e) => e.target.style.borderColor = 'var(--border-input)'} />

            </div>

            <button type="submit" className="btn-primary" style={{ width: '100%', gap: '0.5rem' }}>
              Join Waitlist
              <ArrowRight size={16} />
            </button>

            <p className="caption" style={{ marginTop: '1rem', textAlign: 'center', color: 'var(--text-muted)' }}>
              We'll reach out within 1-2 business days with membership details and next steps.
            </p>
          </form>
        </div>
      </section>

      {/* Alternative Contact */}
      <section className="section" style={{ background: 'var(--bg-page)' }}>
        <div className="container !mx-[10px]" style={{ textAlign: 'center' }}>
          <h2 className="heading-2" style={{ marginBottom: '1rem' }}>
            Have questions about membership?
          </h2>
          <p className="body-medium" style={{ marginBottom: '2rem', color: 'var(--text-secondary)' }}>
            Reach out directly and we'll answer all your questions
          </p>
          <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap' }}>
            <a href="mailto:hello@thevecllc.com" className="btn-secondary">
              Email Us
            </a>
            <Link to="/contact" className="btn-secondary">
              Contact Page
            </Link>
          </div>
        </div>
      </section>
    </div>);

};

export default JoinEWCPage;